import { logger } from '../services/logger.ts';
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { supabase } from '../lib/db.ts';
import { Turnstile, type TurnstileRef } from '../components/common/Turnstile.tsx';

export function ResetPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileRef>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    // Wait for Turnstile verification
    if (!captchaToken) {
      setLoading(false);
      setError('Please complete the security verification above before requesting a password reset.');
      return;
    }

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
        // Alternative: Use Supabase's default route that might not require configuration
        // redirectTo: `${window.location.origin}/auth/confirm`,
        // For testing: try with localhost if you're on development
        // redirectTo: 'http://localhost:5173/update-password',
      });

      if (resetError) throw resetError;

      setSuccess('Password reset link has been sent to your email');
      setEmail('');
    } catch (err) {
      logger.error('Error requesting password reset:', err);
      setError(err instanceof Error ? err.message : 'Failed to send reset link');
      // Reset CAPTCHA on error
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Your Password</h1>
          <p className="text-gray-600">Enter your email to receive password reset instructions</p>
        </div>

        {error && (
          <div className={`mb-6 p-4 rounded ${
            error.includes('complete the security verification') || error.includes('Security verification')
              ? 'bg-yellow-50 border border-yellow-400 text-yellow-800'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                  placeholder="Enter your email address"
                  required
                  disabled={loading}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                We'll send you an email with instructions to reset your password.
              </p>
            </div>

            {/* Cloudflare Turnstile verification - visible to users */}
            <div className="flex flex-col items-center space-y-2">
              <Turnstile
                ref={captchaRef}
                size="normal"
                onVerify={(token) => {
                  setCaptchaToken(token);
                  // Clear any verification-related errors when successful
                  if (error && (error.includes('Security verification') || error.includes('complete the security verification'))) {
                    setError(null);
                  }
                }}
                onError={() => {
                  setCaptchaToken(null);
                  setError('Security verification failed. Please click the checkbox above to retry.');
                }}
                onExpire={() => {
                  setCaptchaToken(null);
                  setError('Security verification expired. Please complete the verification above.');
                }}
              />
            </div>

            <div className="flex justify-end gap-4">
              <Link
                to="/account/settings"
                className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading || !captchaToken}
                className="bg-wayja-teal text-white px-6 py-2 rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : !captchaToken ? 'Complete verification' : 'Send Reset Link'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}